import { filter, flatMap, uniq } from 'lodash';
import type { MessageTranslator } from '@oneflowab/pomes';
import { ALL_COUNTRIES } from '@app/components/eid-country-picker';
import { EID_SIGNING_METHOD, EID_HUB_ITEM_SIGNING_METHOD_KEY } from './constants';

/**
 * Returns a list of electronic IDs available in the EID Hub.
 * Each item includes properties such as key, title, enabled status, visibility, countries, and allowed signing methods.
 *
 * @param {MessageTranslator} message - Function to translate messages.
 * @param {string} [selectedCountry] - Optional country filter to limit the list to specific countries.
 * @returns {Array} List of electronic IDs with their properties.
 */

export const getEidHubList = (message: MessageTranslator, selectedCountry?: string) => {
  const ADES_TOOLTIP_MESSAGE = message({
    id: 'Advanced e-signature with verified identity and strong legal assurance.',
    comment: 'Description for an advanced electronic signature',
  });
  const QES_TOOLTIP_MESSAGE = message({
    id: 'Qualified electronic signature with the highest legal assurance. May incur additional costs.',
    comment: 'Description for a qualified electronic signature',
  });
  let eidList = [
    {
      key: 'eidHubBeBeidSign',
      title: '.beID',
      isEnabled: true,
      isVisible: true,
      countries: [
        'BE',
      ],
      signingMethods: [
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.BELGIAN_EID_ADES,
          label: EID_SIGNING_METHOD.AdES,
          description: ADES_TOOLTIP_MESSAGE,
        },
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.BELGIAN_EID_QES,
          label: EID_SIGNING_METHOD.QES,
          description: QES_TOOLTIP_MESSAGE,
        },
      ],
    },
    {
      key: 'eidHubEeEsteidSign',
      title: 'Estonian ID card',
      isEnabled: true,
      isVisible: true,
      countries: [
        'EE',
      ],
      signingMethods: [
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.ESTONIAN_EID_ADES,
          label: EID_SIGNING_METHOD.AdES,
          description: ADES_TOOLTIP_MESSAGE,
        },
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.ESTONIAN_EID_QES,
          label: EID_SIGNING_METHOD.QES,
          description: QES_TOOLTIP_MESSAGE,
        },
      ],
    },
    {
      key: 'eidHubEeEsteidMobileSign',
      title: 'Estonian Mobile-ID',
      isEnabled: true,
      isVisible: true,
      countries: [
        'EE',
      ],
      signingMethods: [
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.ESTONIAN_MOBILE_EID_ADES,
          label: EID_SIGNING_METHOD.AdES,
          description: ADES_TOOLTIP_MESSAGE,
        },
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.ESTONIAN_MOBILE_EID_QES,
          label: EID_SIGNING_METHOD.QES,
          description: QES_TOOLTIP_MESSAGE,
        },
      ],
    },
    {
      key: 'eidHubFrejaSign',
      title: 'Freja eID',
      isEnabled: true,
      isVisible: true,
      countries: [
        'DK',
        'NO',
        'SE',
        'UA',
        'GB',
        'EU',
      ],
      signingMethods: [
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.FREJA_EID_ADES,
          label: EID_SIGNING_METHOD.AdES,
          description: ADES_TOOLTIP_MESSAGE,
        },
      ],
    },
    {
      key: 'eidHubFiFtnSign',
      title: 'Finnish Trust Network',
      isEnabled: true,
      isVisible: true,
      countries: [
        'FI',
      ],
      signingMethods: [
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.FINNISH_EID_ADES,
          label: EID_SIGNING_METHOD.AdES,
          description: ADES_TOOLTIP_MESSAGE,
        },
      ],
    },
    {
      key: 'eidHubNlIdinSign',
      title: 'iDIN',
      isEnabled: true,
      isVisible: true,
      countries: [
        'NL',
      ],
      signingMethods: [
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.IDIN_EID_ADES,
          label: EID_SIGNING_METHOD.AdES,
          description: ADES_TOOLTIP_MESSAGE,
        },
      ],
    },
    {
      key: 'eidHubBeItsmeSign',
      title: 'itsme',
      isEnabled: true,
      isVisible: true,
      countries: [
        'BE',
      ],
      signingMethods: [
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.ITSME_EID_ADES,
          label: EID_SIGNING_METHOD.AdES,
          description: ADES_TOOLTIP_MESSAGE,
        },
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.ITSME_EID_QES,
          label: EID_SIGNING_METHOD.QES,
          description: QES_TOOLTIP_MESSAGE,
        },
      ],
    },
    {
      key: 'eidHubDkMitidSign',
      title: 'MitID',
      isEnabled: true,
      isVisible: true,
      countries: [
        'DK',
      ],
      signingMethods: [
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.MITID_EID_ADES,
          label: EID_SIGNING_METHOD.AdES,
          description: ADES_TOOLTIP_MESSAGE,
        },
      ],
    },
    {
      key: 'eidHubNoBankidSign',
      title: message({
        id: 'Norwegian BankID',
        comment: 'Title for an electronic ID',
      }),
      isEnabled: true,
      isVisible: true,
      countries: [
        'NO',
      ],
      signingMethods: [
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.NORWEGIAN_BANKID_ADES,
          label: EID_SIGNING_METHOD.AdES,
          description: ADES_TOOLTIP_MESSAGE,
        },
      ],
    },
    {
      key: 'eidHubDeNpaSign',
      title: 'Personalausweis',
      description: message({
        id: 'coming soon',
        comment: 'Description for an electronic ID',
      }),
      isEnabled: false,
      isVisible: false,
      countries: [
        'DE',
      ],
      signingMethods: [
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.GERMAN_NPA_ADES,
          label: EID_SIGNING_METHOD.AdES,
          description: ADES_TOOLTIP_MESSAGE,
        },
      ],
    },
    {
      key: 'eidHubSmartidSign',
      title: 'Smart-ID',
      isEnabled: true,
      isVisible: true,
      countries: [
        'EE',
        'LV',
        'LT',
      ],
      signingMethods: [
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.SMARTID_EID_ADES,
          label: EID_SIGNING_METHOD.AdES,
          description: ADES_TOOLTIP_MESSAGE,
        },
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.SMARTID_EID_QES,
          label: EID_SIGNING_METHOD.QES,
          description: QES_TOOLTIP_MESSAGE,
        },
      ],
    },
    {
      key: 'eidHubSeBankidSign',
      title: message({
        id: 'Swedish BankID',
        comment: 'Title for an electronic ID',
      }),
      description: message({
        id: 'coming soon',
        comment: 'Description for an electronic ID',
      }),
      isEnabled: false,
      isVisible: false,
      countries: [
        'SE',
      ],
      signingMethods: [
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.SWEDISH_BANKID_ADES,
          label: EID_SIGNING_METHOD.AdES,
          description: ADES_TOOLTIP_MESSAGE,
        },
      ],
    },
    {
      key: 'eidHubSimplySignSign',
      title: 'SimplySign',
      isEnabled: true,
      isVisible: true,
      countries: [
        'PL',
      ],
      signingMethods: [
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.SIMPLY_SIGN_ADES,
          label: EID_SIGNING_METHOD.AdES,
          description: ADES_TOOLTIP_MESSAGE,
        },
        {
          key: 'simplySignQes',
          label: EID_SIGNING_METHOD.QES,
          description: QES_TOOLTIP_MESSAGE,
        },
      ],
    },
    {
      key: 'eidHubZealidSign',
      title: 'ZealID',
      isEnabled: true,
      isVisible: true,
      countries: [
        'DK',
        'FI',
        'NO',
        'SE',
      ],
      signingMethods: [
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.ZEALID_QES,
          label: EID_SIGNING_METHOD.QES,
          description: QES_TOOLTIP_MESSAGE,
        },
      ],
    },
    {
      key: 'eidHubEvrotrustSign',
      title: 'Evrotrust',
      isEnabled: true,
      isVisible: true,
      countries: [
        'DK',
        'FI',
        'NO',
        'SE',
      ],
      signingMethods: [
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.EVROTRUST_QES,
          label: EID_SIGNING_METHOD.QES,
          description: QES_TOOLTIP_MESSAGE,
        },
      ],
    },
    {
      key: 'eidHubDTrustSignMeSign',
      title: 'sign-me',
      isEnabled: true,
      isVisible: true,
      countries: [
        'DE',
        'DK',
        'FI',
        'NO',
        'SE',
      ],
      signingMethods: [
        {
          key: EID_HUB_ITEM_SIGNING_METHOD_KEY.SIGN_ME_QES,
          label: EID_SIGNING_METHOD.QES,
          description: QES_TOOLTIP_MESSAGE,
        },
      ],
    },
  ];

  if (selectedCountry && selectedCountry !== ALL_COUNTRIES) {
    eidList = eidList.filter((item) => item.countries.includes(selectedCountry));
  }

  return eidList;
};

export const getUniqueCountries = () => {
  const message = ({ id }: { id: string }) => id;

  const items = getEidHubList(message);

  // Filter items based on isVisible conditions
  const filteredItems = filter(items, {
    isVisible: true,
  });

  // Then, extract unique countries from the filtered items
  const countries = flatMap(filteredItems, (item) => item.countries);
  const uniqueCountries = uniq(countries);

  return uniqueCountries;
};
